import * as React from "react";
import { FC } from "react";

import { Box } from "@mui/material";
import Grid from "@mui/material/Grid";
import { SxProps, Theme } from "@mui/material/styles";

import { QuestionConfirmation } from "../QuestionConfirmation/QuestionConfirmation";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { Delete } from "@/icons/outlined";

interface DeleteConfirmationsProps {
  confirmation: () => void;
  shouldConfirm?: boolean;
  disabled?: boolean;
  hasIcon?: boolean;
  customMessages?: {
    title: string;
    description?: string;
    cancel: string;
    confirm: string;
  };
}

export const DeleteConfirmations: FC<DeleteConfirmationsProps> = ({
  confirmation,
  shouldConfirm = true,
  disabled = false,
  hasIcon = true,
  customMessages,
}) => {
  const { t } = useTranslate();
  const [displayConfirmation, setDisplayConfirmation] = React.useState(false);

  const handleClick = React.useCallback(() => {
    if (shouldConfirm) {
      setDisplayConfirmation((prevValue) => !prevValue);
    } else {
      confirmation();
    }
  }, [shouldConfirm, setDisplayConfirmation, confirmation]);

  const handleConfirm = React.useCallback(() => {
    confirmation();
    setDisplayConfirmation(false);
  }, [confirmation]);

  return (
    <Grid container justifyContent="flex-end">
      <Box data-test="delete-confirmation" sx={{ flexDirection: "column" }}>
        {displayConfirmation && (
          <Box
            data-test="delete-confirmation-overlay"
            sx={(theme) => ({
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              zIndex: 200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 2,
              backgroundColor: theme.palette.surface.neutral.supershy,
              borderRadius: theme.radius(1),
            })}
          >
            <Box sx={{ maxWidth: 400 }}>
              <QuestionConfirmation
                icon={hasIcon ? <Delete /> : undefined}
                title={customMessages?.title || t("dashboard", "donationForm.areYouSure")}
                description={customMessages?.description}
                cancel={{
                  label: customMessages?.cancel || t("dashboard", "donationForm.cancel"),
                  onClick: handleClick,
                }}
                confirm={{
                  label: customMessages?.confirm || t("dashboard", "donationForm.yes"),
                  onClick: handleConfirm,
                }}
              />
            </Box>
          </Box>
        )}

        {!displayConfirmation && (
          <DashboardIconButton
            size="small"
            onClick={handleClick}
            vibe="neutral"
            data-test="delete-button"
            disabled={disabled}
          >
            <Delete color="error" />
          </DashboardIconButton>
        )}
      </Box>
    </Grid>
  );
};

// Froala's editor layer paints above an overlay that is not scoped to this card.
export const deleteConfirmationAnchorSx: SxProps<Theme> = {
  position: "relative",
  isolation: "isolate",
};
